import type { Connector, DeviceAuthorization, DeviceAuthorizationState } from "../../../api/connectorsApi";
import type { UiError } from "../../../errors/uiError";
import { defineStateMachine, IllegalStateTransitionError } from "../../../shared/state/stateMachine";
import { parseUserCode, type UserCode } from "./userCode";

/**
 * The pairing page's flow (connector-protocol.md, "Pairing"): enter or receive a user code,
 * look it up, show the device, then approve or deny it once. A code that was decided
 * already, or that expired, can only be looked at.
 */

export type PairingDecision = "approve" | "deny";

/** A device authorization that can no longer be decided. */
export type ClosedAuthorizationState = Exclude<DeviceAuthorizationState, "pending">;

export type PairingState =
  | { readonly status: "enteringCode" }
  | { readonly status: "lookingUp"; readonly userCode: UserCode }
  | { readonly status: "lookupFailed"; readonly userCode: UserCode; readonly error: UiError }
  | { readonly status: "awaitingDecision"; readonly authorization: DeviceAuthorization }
  | {
      readonly status: "deciding";
      readonly authorization: DeviceAuthorization;
      readonly decision: PairingDecision;
    }
  | {
      readonly status: "decisionFailed";
      readonly authorization: DeviceAuthorization;
      readonly decision: PairingDecision;
      readonly error: UiError;
    }
  | { readonly status: "approved"; readonly authorization: DeviceAuthorization; readonly connector: Connector }
  | { readonly status: "denied"; readonly authorization: DeviceAuthorization }
  /** Decided before (here, in another tab or by someone else) or expired. */
  | {
      readonly status: "alreadyClosed";
      readonly authorization: DeviceAuthorization;
      readonly closedState: ClosedAuthorizationState;
    };

export type PairingEvent =
  | { readonly type: "codeSubmitted"; readonly userCode: UserCode }
  | { readonly type: "lookupSucceeded"; readonly authorization: DeviceAuthorization }
  | { readonly type: "lookupFailed"; readonly error: UiError }
  | { readonly type: "decisionStarted"; readonly decision: PairingDecision }
  | { readonly type: "approvalSucceeded"; readonly connector: Connector }
  | { readonly type: "denialSucceeded" }
  | { readonly type: "decisionFailed"; readonly error: UiError }
  /** The code's expiry time passed while the page was open. */
  | { readonly type: "codeExpired" }
  | { readonly type: "otherCodeRequested" };

const MACHINE_NAME = "connector pairing";

/** The server's name of the state machine behind `illegal_state_transition` on a decision. */
const SERVER_DEVICE_AUTHORIZATION_MACHINE = "device_authorization";

const CLOSED_STATES: readonly ClosedAuthorizationState[] = ["approved", "denied", "expired"];

const codeSubmitted = (_state: PairingState, event: { readonly userCode: UserCode }): PairingState => ({
  status: "lookingUp",
  userCode: event.userCode,
});

const otherCodeRequested = (): PairingState => ({ status: "enteringCode" });

const decisionStarted = (
  state: { readonly authorization: DeviceAuthorization },
  event: { readonly decision: PairingDecision },
): PairingState => ({ status: "deciding", authorization: state.authorization, decision: event.decision });

const codeExpired = (state: { readonly authorization: DeviceAuthorization }): PairingState =>
  closed(state.authorization, "expired");

export const transitionPairing = defineStateMachine<PairingState, PairingEvent>(MACHINE_NAME, {
  enteringCode: { codeSubmitted },
  lookingUp: {
    lookupSucceeded: (_state, event) =>
      event.authorization.state === "pending"
        ? { status: "awaitingDecision", authorization: event.authorization }
        : closed(event.authorization, event.authorization.state),
    lookupFailed: (state, event) => ({ status: "lookupFailed", userCode: state.userCode, error: event.error }),
  },
  lookupFailed: { codeSubmitted, otherCodeRequested },
  awaitingDecision: { decisionStarted, codeExpired, otherCodeRequested },
  deciding: {
    approvalSucceeded: (state, event) => {
      requireDecision(state, "approve", "approvalSucceeded");
      return { status: "approved", authorization: { ...state.authorization, state: "approved" }, connector: event.connector };
    },
    denialSucceeded: (state) => {
      requireDecision(state, "deny", "denialSucceeded");
      return { status: "denied", authorization: { ...state.authorization, state: "denied" } };
    },
    decisionFailed: (state, event) => {
      const closedState = closedStateOfDecisionError(event.error);
      return closedState === null
        ? { status: "decisionFailed", authorization: state.authorization, decision: state.decision, error: event.error }
        : closed(state.authorization, closedState);
    },
    // The request is already on its way; the server's answer decides what happened.
    codeExpired: (state) => state,
  },
  decisionFailed: { decisionStarted, codeSubmitted, codeExpired, otherCodeRequested },
  approved: { otherCodeRequested },
  denied: { otherCodeRequested },
  alreadyClosed: { codeSubmitted, otherCodeRequested },
});

/** Where the page starts: looking up a well-formed code from the address, else asking for one. */
export function initialPairingState(codeFromAddress: string | null): PairingState {
  const parsed = parseUserCode(codeFromAddress ?? "");
  return parsed.kind === "valid" ? { status: "lookingUp", userCode: parsed.userCode } : { status: "enteringCode" };
}

/** The authorization the page shows, in every state that has one. */
export function authorizationOfState(state: PairingState): DeviceAuthorization | null {
  return "authorization" in state ? state.authorization : null;
}

/**
 * A decision the server refused because the code was decided or expired meanwhile: the
 * problem names the state it found (`illegal_state_transition`, details `state_machine`
 * and `state`). Any other failure is `null` and shown as an error.
 */
export function closedStateOfDecisionError(error: UiError): ClosedAuthorizationState | null {
  if (error.kind !== "problem" || error.problem.code !== "illegal_state_transition") {
    return null;
  }
  const { state_machine: machine, state } = error.problem.details;
  if (machine !== SERVER_DEVICE_AUTHORIZATION_MACHINE) {
    return null;
  }
  return CLOSED_STATES.find((closedState) => closedState === state) ?? null;
}

function closed(authorization: DeviceAuthorization, closedState: ClosedAuthorizationState): PairingState {
  return { status: "alreadyClosed", authorization: { ...authorization, state: closedState }, closedState };
}

function requireDecision(
  state: Extract<PairingState, { status: "deciding" }>,
  expected: PairingDecision,
  eventType: PairingEvent["type"],
): void {
  if (state.decision !== expected) {
    throw new IllegalStateTransitionError(MACHINE_NAME, `${state.status} (${state.decision})`, eventType);
  }
}
